const { Currency } = require('@sigmasms/shared-types')

const { SENDING_TYPES } = require('../constants/_sendings')
const { DOCUMENT_TEMPLATES_TYPES } = require('../constants/_documentTemplates')
const { NOTIFICATIONS_AVAILABLE_TYPES } = require('../constants/_notifications')

const { isUUID, isHEXColor, isSimpleObject } = require('../validators')

const { $defaulter, isArrayOfUUIDs } = require('./utils')
const { MappedProcessorSync } = require('../utils/MappedProcessor')

const { NOTIFICATIONS_TEMPLATES, NOTIFICATIONS_TEMPLATES_MANAGER } = require('./notifications')

const paymentsMethodsDefault = {
  tinkoff: {
    TerminalKey: '',
    Password: '',
    ekamToken: ''
  },
  mandarin: {
    merchantId: '',
    secret: '',
    ekamToken: '',
    includeFiscalInformation: false
  },
  stripe: {
    apikey: ''
  }
}

const SETTINGS_SITE_SCHEMA = {
  'misc.rootId': {
    $default: '',
    $handler: v => '' + (v || '') || undefined,
    $cleaner: value => typeof value === 'string' && value ? value : undefined,
    $defaulter
  },

  'resellers.domains': {
    $default: {},
    $handler: v => {
      if (typeof v === 'object' && v !== null) {
        const result = {}
        Object.keys(v).forEach(key => {
          if (v[key] && isUUID(v[key])) {
            result[key] = v[key]
          }
        })

        return result
      }

      return {}
    },
    $cleaner: value => {
      const result = {}

      if (isSimpleObject(value)) {
        for (const key in value) {
          const element = value[key]
          if (element && isUUID(element)) {
            result[key] = element
          }
        }
      }

      return result
    },
    $defaulter
  },

  'auth.registration.defaults': {
    $default: {
      tariffs: [],
      routingGroups: [],
      messageRuleGroups: [],
      patterns: [],
      sendernames: [],
      allowDuplicates: false,
      sendernamesCheck: SENDING_TYPES,
      patternsCheck: SENDING_TYPES,
      balance: 0,
      currency: Currency.RUB
    },
    $handler: (v, schema) => {
      if (!v) {
        v = {}
      }

      let balance = parseFloat(v.balance)
      if (isNaN(balance) || balance < 0) {
        balance = schema.$default.balance
      }

      const isArrayOfUUIDs = value => Array.isArray(value) && value.filter(isUUID)

      return {
        tariffs: v && isArrayOfUUIDs(v.tariffs) || schema.$default.tariffs,
        routingGroups: v && isArrayOfUUIDs(v.routingGroups) || schema.$default.routingGroups,
        messageRuleGroups: v && isArrayOfUUIDs(v.messageRuleGroups) || schema.$default.messageRuleGroups,
        patterns: v && isArrayOfUUIDs(v.patterns) || schema.$default.patterns,
        sendernames: v && isArrayOfUUIDs(v.sendernames) || schema.$default.sendernames,
        allowDuplicates: v && v.allowDuplicates === undefined ? schema.$default.allowDuplicates : !!v.allowDuplicates,
        sendernamesCheck:
          v && Array.isArray(v.sendernamesCheck)
            ? v.sendernamesCheck.reduce((result, type) => {
              if (~SENDING_TYPES.indexOf(type) && !~result.indexOf(type)) {
                result.push(type)
              }

              return result
            }, [])
            : schema.$default.sendernamesCheck,
        patternsCheck:
          v && Array.isArray(v.patternsCheck)
            ? v.patternsCheck.reduce((result, type) => {
              if (~SENDING_TYPES.indexOf(type) && !~result.indexOf(type)) {
                result.push(type)
              }

              return result
            }, [])
            : schema.$default.patternsCheck,
        balance,
        currency: v && Object.values(Currency).includes(v.currency) ? v.currency : schema.$default.currency
      }
    },
    $cleaner: (value, { $default }) => {
      const result = {}

      if (isSimpleObject(value)) {
        for (const key in $default) {
          const element = value[key]
          switch (key) {
            case 'tariffs':
            case 'routingGroups':
            case 'messageRuleGroups':
            case 'patterns':
            case 'sendernames': {
              const arrayWithUUIDs = isArrayOfUUIDs(element)
              if (arrayWithUUIDs) {
                result[key] = arrayWithUUIDs
              }
              break
            }

            case 'allowDuplicates': {
              if (typeof element === 'boolean') {
                result[key] = element
              }
              break
            }

            case 'sendernamesCheck': {
              if (Array.isArray(element)) {
                result[key] = []
                for (const type of element) {
                  if (~SENDING_TYPES.indexOf(type) && !~result[key].indexOf(type)) {
                    result[key].push(type)
                  }
                }
              }
              break
            }

            case 'patternsCheck': {
              if (Array.isArray(element)) {
                result[key] = []
                for (const type of element) {
                  if (~SENDING_TYPES.indexOf(type) && !~result[key].indexOf(type)) {
                    result[key].push(type)
                  }
                }
              }
              break
            }

            case 'balance': {
              const balance = parseFloat(element)
              if (!isNaN(balance) || balance >= 0) {
                result[key] = balance
              }
              break
            }

            case 'currency': {
              const currency = element
              if (currency && Object.values(Currency).includes(currency)) {
                result[key] = currency
              }
              break
            }
          }
        }
      }

      return result
    },
    $defaulter
  },

  'frontend.title': {
    $default: 'Sigma SMS',
    $handler: v => '' + (v || '') || undefined,
    $cleaner: value => typeof value === 'string' && value ? value : undefined,
    $defaulter
  },
  'frontend.colors': {
    $default: {
      primary: '#1976D2',
      secondary: '#FFA726',
      accent: '#82B1FF',
      error: '#FF5252',
      info: '#2196F3',
      success: '#4CAF50',
      warning: '#FFC107'
    },
    $handler: (v, schema) =>
      Object.keys(schema.$default).reduce((result, colorType) =>
        Object.assign(result, {
          [colorType]: isHEXColor(v && v[colorType]) || schema.$default[colorType]
        }),
      {}),
    $cleaner: (value, { $default }) => {
      const result = {}

      if (isSimpleObject(value)) {
        for (const key in $default) {
          const element = value[key]
          if (isHEXColor(element)) {
            result[key] = element
          }
        }
      }

      return result
    },
    $defaulter
  },
  'frontend.documents.privacyPolicy': {
    $default: null,
    $handler: v => isUUID(v) || undefined,
    $cleaner: value => isUUID(value) || value === null ? value : undefined,
    $defaulter
  },
  'frontend.documents.publicOffer': {
    $default: null,
    $handler: v => isUUID(v) || undefined,
    $cleaner: value => isUUID(value) || value === null ? value : undefined,
    $defaulter
  },
  'frontend.jivosite': {
    $default: '', // FEJECz7F12
    $handler: v => typeof v === 'string' ? v : undefined,
    $cleaner: value => typeof value === 'string' ? value : undefined,
    $defaulter
  },

  'notifications.enabled': {
    $default: [ NOTIFICATIONS_AVAILABLE_TYPES.email ],
    $multiple: true,
    $options: NOTIFICATIONS_AVAILABLE_TYPES.map($value => ({ $value }))
  },
  'notifications.email.smtp': {
    $default: {
      host: '',
      port: 465,
      login: '',
      password: '',
      from: ''
    },
    $handler: (v, schema) => {
      return Object.keys(schema.$default).reduce((result, key) => {
        switch (key) {
          case 'port': {
            let value =
              v && (typeof v[key] === 'number' && v[key] || typeof v[key] === 'string' && parseInt(v[key]))
            if (!value || isNaN(value)) {
              value = schema.$default[key]
            }

            result[key] = value
            break
          }
          default:
            result[key] = '' + (v && v[key] || schema.$default[key])
            break
        }

        return result
      }, {})
    },
    $cleaner: (value, { $default }) => {
      const result = {}

      if (isSimpleObject(value)) {
        for (const key in $default) {
          const element = value[key]
          switch (key) {
            case 'host':
            case 'login':
            case 'password':
            case 'from': {
              if (typeof element === 'string') {
                result[key] = element
              }
              break
            }

            case 'port': {
              if (typeof element === 'number') {
                result[key] = element
              }
              break
            }
          }
          result
        }
      }

      return result
    },
    $defaulter
  },
  'notifications.email.templates': {
    $default: {
      // Остается для совместимости, новые настройки хранятся в balance.limit
      'billings.limit': {
        subject: 'Критическое значение баланса в личном кабинете по адресу: ${domain}',
        body: 'Уважаемый Клиент,<br><br>Внимание!<br><br>Ваш баланс преодолел критическое значение.<br>Доступно к использованию: ${balance}<br>Напоминаем о необходимости его пополнить.<br>Логин: ${username} на <a href="${url}">${domain}</a><br><br>Свяжитесь со своим менеджером или напишите по адресу ${email}.<br><br>С уважением ${title}'
      },
      'auth.registration': {
        subject: 'Спасибо за регистрацию на платформе СМС рассылок',
        body: 'Спасибо за регистрацию на платформе СМС рассылок.<br><br>Ваш логин и пароль для входа в платформу:<br>Логин: {{username}}<br>Пароль: {{password}}{{#if balance}}<br><br>Внимание!<br>Вам начислен тестовый баланс {{balance}} рублей. Начинайте прямо сейчас!{{/if}}'
      },
      'auth.recover': {
        subject: 'Запрос смены пароля на ${domain}',
        body: 'Уважаемый Клиент,<br><br>Вы запросили смену пароля.<br>Для смены пароля перейдите по ссылке: <a href="${domain}/#/recover/${key}">${domain}/#/recover/${key}</a>.<br><br>Если вы не делали запрос на смену пароля, рекомендуем вам сменить пароль через личный кабинет.'
      }
    },
    $handler: (v, schema) => {
      return Object.keys(schema.$default).reduce((result, key) => {
        result[key] = Object.assign({}, schema.$default[key])

        if (v && v[key]) {
          // result[key].to = '' + (v[key].to || result[key].to || '')
          result[key].subject = '' + (v[key].subject || result[key].subject || '')
          result[key].body = '' + (v[key].body || result[key].body || '')
        }

        return result
      }, {})
    },
    $cleaner: (value, { $default }) => {
      const result = {}

      if (isSimpleObject(value)) {
        for (const key in $default) {
          const element = value[key]
          switch (key) {
            case 'billings.limit':
            case 'auth.registration':
            case 'auth.recover': {
              const template = {}
              const props = [ 'subject', 'body' ]
              for (const prop in element) {
                const propValue = element[prop]
                if (~props.indexOf(prop) && typeof propValue === 'string') {
                  template[prop] = propValue
                }
              }
              if (Object.keys(template).every(prop => ~props.indexOf(prop))) {
                result[key] = template
              }
              break
            }
          }
          result
        }
      }

      return result
    },
    $defaulter
  },
  'notifications.email.templates.manager': {
    $default: {
      // Остается для совместимости, новые настройки хранятся в balance.limit
      'billings.limit': {
        subject: 'Критическое значение баланса на ${domain}',
        body: 'Баланс пользователя ${username} преодолел критическое значение<br>Баланс пользователя - ${balance}'
      },
      // Вроде не использется
      'providers.health': {
        subject: 'Состояние провайдера {{title}}: {{status}}',
        body: 'Провайдер {{title}} имеет статус {{status}}. {{#if error}}\nОшибка: {{error}}{{/if}}'
      }
    },
    $handler: (v, schema) => {
      return Object.keys(schema.$default).reduce((result, key) => {
        result[key] = Object.assign({}, schema.$default[key])

        if (v && v[key]) {
          // result[key].to = '' + (v[key].to || result[key].to || '')
          result[key].subject = '' + (v[key].subject || result[key].subject || '')
          result[key].body = '' + (v[key].body || result[key].body || '')
        }

        return result
      }, {})
    },
    $cleaner: (value, { $default }) => {
      const result = {}

      if (isSimpleObject(value)) {
        for (const key in $default) {
          const element = value[key]
          switch (key) {
            case 'billings.limit':
            case 'providers.health': {
              const template = {}
              const props = [ 'subject', 'body' ]
              for (const prop in element) {
                const propValue = element[prop]
                if (~props.indexOf(prop) && typeof propValue === 'string') {
                  template[prop] = propValue
                }
              }
              if (Object.keys(template).every(prop => ~props.indexOf(prop))) {
                result[key] = template
              }
              break
            }
          }
          result
        }
      }

      return result
    },
    $defaulter
  },
  'notifications.sms.senderUserId': {
    $default: null,
    $handler: v => isUUID(v) || undefined,
    $cleaner: value => isUUID(value) || value === null ? value : undefined,
    $defaulter
  },
  'notifications.sms.sendername': {
    $default: '',
    $handler: v => typeof v === 'string' ? v : undefined,
    $cleaner: value => typeof value === 'string' ? value : undefined,
    $defaulter
  },
  'notifications.sms.templates': {
    $default: {},
    $handler: (v, schema) => {
      return Object.keys(schema.$default).reduce((result, key) => {
        result[key] = Object.assign({}, schema.$default[key])

        if (v && v[key]) {
          result[key].body = '' + (v[key].body || result[key].body || '')
        }

        return result
      }, {})
    },
    $cleaner: (value, { $default }) => {
      const result = {}

      if (isSimpleObject(value)) {
        for (const key in $default) {
          const element = value[key]
          switch (key) {
            default: {
              const template = {}
              const props = [ 'body', 'subject' ]
              for (const prop in element) {
                const propValue = element[prop]
                if (~props.indexOf(prop) && typeof propValue === 'string') {
                  template[prop] = propValue
                }
              }
              if (Object.keys(template).every(prop => ~props.indexOf(prop))) {
                result[key] = template
              }
              break
            }
          }
          result
        }
      }

      return result
    },
    $defaulter
  },
  'notifications.events.billings.limit': {
    $default: [
      {
        balance: 0,
        types: [ NOTIFICATIONS_AVAILABLE_TYPES.email ],
        includeOverdraft: false,
        invoice: false
      },
      {
        balance: 0,
        types: [ NOTIFICATIONS_AVAILABLE_TYPES.email ],
        includeOverdraft: true,
        invoice: false
      },
      {
        balance: 100,
        types: [ NOTIFICATIONS_AVAILABLE_TYPES.email ],
        includeOverdraft: false,
        invoice: false
      },
      {
        balance: 100,
        types: [ NOTIFICATIONS_AVAILABLE_TYPES.email ],
        includeOverdraft: true,
        invoice: false
      }
    ],
    $handler: (v, schema) => {
      // Певращение старого формата в новый
      if (!Array.isArray(v) && typeof v === 'object' && v !== null) {
        const reMatchDigits = /^\d+$/
        v = Object.keys(v).reduce((result, key) => {
          if (typeof key === 'string' && reMatchDigits.test(key) || typeof key === 'number') {
            result.push({
              balance: key,
              types: v[key].filter(notificationType => ~NOTIFICATIONS_AVAILABLE_TYPES.indexOf(notificationType)),
              includeOverdraft: false,
              invoice: false
            })
          }

          return result
        }, [])
      }

      if (!Array.isArray(v)) {
        v = schema.$default
      }

      const result = []
      for (let index = 0; index < v.length; index++) {
        const limit = v[index]
        limit.balance = parseFloat(limit.balance)
        if (isNaN(limit.balance)) {
          continue
        }

        if (Array.isArray(limit.types)) {
          limit.types = limit.types.filter(notificationType => ~NOTIFICATIONS_AVAILABLE_TYPES.indexOf(notificationType))
        }
        if (!Array.isArray(limit.types) || !limit.types.length) {
          continue
        }

        limit.includeOverdraft = !!limit.includeOverdraft

        let invoice = false
        if (!Array.isArray(limit.invoice) && limit.invoice && typeof limit.invoice === 'object') {
          if (limit.invoice.data && limit.invoice.data.SERVICE_PRICE) {
            limit.invoice.data.SERVICE_PRICE = parseFloat(limit.invoice.data.SERVICE_PRICE)
            if (isNaN(limit.invoice.data.SERVICE_PRICE)) {
              limit.invoice.data = {
                SERVICE_PRICE: undefined
              }
            }
          } else {
            limit.invoice.data = {
              SERVICE_PRICE: undefined
            }
          }
          if (
            isUUID(limit.invoice.requisiteId) &&
            isUUID(limit.invoice.companyId) &&
            limit.invoice.type === DOCUMENT_TEMPLATES_TYPES.invoice &&
            limit.invoice.data.SERVICE_PRICE
          ) {
            invoice = {
              requisiteId: limit.invoice.requisiteId,
              companyId: limit.invoice.companyId,
              type: limit.invoice.type,
              data: {
                SERVICE_PRICE: limit.invoice.data.SERVICE_PRICE
              }
            }
          }
        }
        limit.invoice = invoice

        result.push(limit)
      }

      return result
    },
    $cleaner: value => {
      let result

      if (Array.isArray(value) && value.length) {
        result = []

        for (const limit of value) {
          limit.balance = parseFloat(limit.balance)
          if (isNaN(limit.balance)) {
            continue
          }

          if (Array.isArray(limit.types)) {
            limit.types = limit.types.filter(notificationType => ~NOTIFICATIONS_AVAILABLE_TYPES.indexOf(notificationType))
          }
          if (!Array.isArray(limit.types) || !limit.types.length) {
            continue
          }

          limit.includeOverdraft = !!limit.includeOverdraft

          let invoice = false
          if (!Array.isArray(limit.invoice) && limit.invoice && typeof limit.invoice === 'object') {
            if (limit.invoice.data && limit.invoice.data.SERVICE_PRICE) {
              limit.invoice.data.SERVICE_PRICE = parseFloat(limit.invoice.data.SERVICE_PRICE)
              if (isNaN(limit.invoice.data.SERVICE_PRICE)) {
                limit.invoice.data = {
                  SERVICE_PRICE: undefined
                }
              }
            } else {
              limit.invoice.data = {
                SERVICE_PRICE: undefined
              }
            }
            if (
              isUUID(limit.invoice.requisiteId) &&
              isUUID(limit.invoice.companyId) &&
              limit.invoice.type === DOCUMENT_TEMPLATES_TYPES.invoice &&
              limit.invoice.data.SERVICE_PRICE
            ) {
              invoice = {
                requisiteId: limit.invoice.requisiteId,
                companyId: limit.invoice.companyId,
                type: limit.invoice.type,
                data: {
                  SERVICE_PRICE: limit.invoice.data.SERVICE_PRICE
                }
              }
            }
          }
          limit.invoice = invoice

          result.push(limit)
        }
      }

      return result
    },
    $defaulter
  },
  'notifications.events.providers.health.recipients': {
    $default: [ '3e194b3e-a2ee-40ce-a986-17fdc918541e' ],
    $handler: v => {
      if (!Array.isArray(v)) {
        return []
      }

      return v.filter(isUUID)
    },
    $cleaner: value => {
      let result

      if (Array.isArray(value)) {
        result = []

        for (const recipient of value) {
          if (isUUID(recipient)) {
            result.push(recipient)
          }
        }
      }

      return result
    },
    $defaulter
  },
  'notifications.extraRecipients': {
    $default: [ '3e194b3e-a2ee-40ce-a986-17fdc918541e' ],
    $handler: v => {
      if (!Array.isArray(v)) {
        return []
      }

      return v.filter(isUUID)
    },
    $cleaner: value => {
      let result

      if (Array.isArray(value)) {
        result = []

        for (const recipient of value) {
          if (isUUID(recipient)) {
            result.push(recipient)
          }
        }
      }

      return result
    },
    $defaulter
  },

  ...NOTIFICATIONS_TEMPLATES,
  ...NOTIFICATIONS_TEMPLATES_MANAGER,

  'payments.methods': {
    $default: paymentsMethodsDefault,
    $handler: v => ({
      tinkoff: {
        TerminalKey: v && v.tinkoff && v.tinkoff.TerminalKey || '',
        Password: v && v.tinkoff && v.tinkoff.Password || '',
        ekamToken: v && v.tinkoff && v.tinkoff.ekamToken || ''
      },
      mandarin: {
        merchantId: v && v.mandarin && v.mandarin.merchantId || '',
        secret: v && v.mandarin && v.mandarin.secret || '',
        ekamToken: v && v.mandarin && v.mandarin.ekamToken || '',
        includeFiscalInformation: v && v.mandarin && v.mandarin.includeFiscalInformation || false
      },
      stripe: {
        apikey: v && v.stripe && v.stripe.apikey || ''
      }
    }),
    $cleaner: (paymentMethodsObject, { $default }) => {
      const toString = v => typeof v === 'string' ? v : ''

      const result = {}

      if (isSimpleObject(paymentMethodsObject)) {
        for (const paymentMethodName in $default) {
          let paymentMethodData = paymentMethodsObject[paymentMethodName]

          let mappedHandlers = null
          switch (paymentMethodName) {
            case 'tinkoff': {
              if (!paymentMethodData) {
                paymentMethodData = paymentMethodsObject['v1.tinkoff']
              }

              mappedHandlers = {
                TerminalKey: toString,
                Password: toString,
                ekamToken: toString
              }
              break
            }
            case 'mandarin': {
              mappedHandlers = {
                merchantId: toString,
                secret: toString,
                ekamToken: toString,
                includeFiscalInformation: v => !!v
              }
              break
            }
            case 'stripe': {
              mappedHandlers = {
                apikey: toString
              }
              break
            }
          }

          if (!mappedHandlers) {
            continue
          }

          result[paymentMethodName] = MappedProcessorSync.process(paymentMethodData, mappedHandlers)
        }
      }

      return result
    },
    $defaulter
  },
  'payments.allowedGlobalMethods': {
    $default: [],
    $multiple: true,
    $options: Object.keys(paymentsMethodsDefault).map($value => ({ $value }))
  },

  'globalBlacklistManager.mainListId': {
    $default: null,
    $handler: v => isUUID(v) || undefined,
    $cleaner: value => isUUID(value) || value === null ? value : undefined,
    $defaulter
  },
  'globalBlacklistManager.temporaryAvailableListId': {
    $default: null,
    $handler: v => isUUID(v) || undefined,
    $cleaner: value => isUUID(value) || value === null ? value : undefined,
    $defaulter
  },

  'apisender.userApiKey': {
    $default: undefined,
    $handler: v => typeof v === 'string' ? v : undefined,
    $cleaner: value => typeof value === 'string' ? value : undefined,
    $defaulter
  },
  'integrations.yclients.applicationCredentials': {
    $default: {
      id: '',
      token: ''
    },
    $cleaner: value => {
      const result = {}

      if (typeof value !== 'object' || value === null) {
        return result
      }

      if (value.id) {
        const id = parseInt(value.id)
        if (!isNaN(id)) {
          result.id = id
        }
      }
      if (typeof value.token === 'string' && value.token) {
        result.token = value.token
      }

      return result
    },
    $defaulter
  }
}

exports.SETTINGS_SITE_SCHEMA = SETTINGS_SITE_SCHEMA

exports.SETTINGS_SITE_SCHEMA_RESELLER = {
  'auth.registration.defaults': Object.assign({}, SETTINGS_SITE_SCHEMA['auth.registration.defaults'], {
    $default: {
      tariffs: SETTINGS_SITE_SCHEMA['auth.registration.defaults'].$default.tariffs,
      balance: SETTINGS_SITE_SCHEMA['auth.registration.defaults'].$default.balance
    }
  }),

  'frontend.title': SETTINGS_SITE_SCHEMA['frontend.title'],
  'frontend.colors': SETTINGS_SITE_SCHEMA['frontend.colors'],
  'frontend.documents.privacyPolicy': SETTINGS_SITE_SCHEMA['frontend.documents.privacyPolicy'],
  'frontend.documents.publicOffer': SETTINGS_SITE_SCHEMA['frontend.documents.publicOffer'],

  'notifications.enabled': SETTINGS_SITE_SCHEMA['notifications.enabled'],
  'notifications.email.smtp': SETTINGS_SITE_SCHEMA['notifications.email.smtp'],
  'notifications.email.templates': SETTINGS_SITE_SCHEMA['notifications.email.templates'],
  'notifications.email.templates.manager': SETTINGS_SITE_SCHEMA['notifications.email.templates.manager'],
  'notifications.sms.senderUserId': SETTINGS_SITE_SCHEMA['notifications.sms.senderUserId'],
  'notifications.sms.sendername': SETTINGS_SITE_SCHEMA['notifications.sms.sendername'],
  'notifications.sms.templates': SETTINGS_SITE_SCHEMA['notifications.sms.templates'],
  'notifications.events.billings.limit': SETTINGS_SITE_SCHEMA['notifications.events.billings.limit'],

  'payments.methods': SETTINGS_SITE_SCHEMA['payments.methods']
}

for (const notificationTemplateSettingPath in NOTIFICATIONS_TEMPLATES) {
  exports.SETTINGS_SITE_SCHEMA_RESELLER[notificationTemplateSettingPath] = SETTINGS_SITE_SCHEMA[notificationTemplateSettingPath]
}
for (const notificationTemplateSettingPath in NOTIFICATIONS_TEMPLATES_MANAGER) {
  exports.SETTINGS_SITE_SCHEMA_RESELLER[notificationTemplateSettingPath] = SETTINGS_SITE_SCHEMA[notificationTemplateSettingPath]
}
